import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { Checkbox } from '../../../../components/forms/Checkbox';
import { UseFormReturn } from 'react-hook-form';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import pluralize from 'pluralize';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import classes from './PostEditorModalFooter.module.scss';
import classNames from 'classnames';
import { AppContext, isFromChromeExtension } from '../../../../providers/AppProvider';
import { PostEditorContextEnum } from '../../../../models/post-editor';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { getErrorsCounter } from '../../helpers/getErrorsCounter';
import { getUploadingMediaCount } from '../../helpers/getUploadingMediaCount';
import { idGenerator } from 'helpers/idGenerator';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';
import { Debugger } from '../../../../components/containers/Debugger';
import { DevDebugContext } from '../../../../providers/DevDebugProvider';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';

export type PostEditorModalFooterViewProps = {
  //
  formInstance: UseFormReturn;
  loading: boolean;
};

export const PostEditorModalFooterView = ({ formInstance, loading }: PostEditorModalFooterViewProps): JSX.Element => {
  const size = useBreakpoints();
  const {
    postEditorContext: { type: context }
  } = useContext(AppContext);
  const { state } = useContext(PostEditorContext);
  const { debugMode } = useContext(DevDebugContext);

  const [totalErrors] = getErrorsCounter(state.postGroups);
  const numberOfPosts = state.postGroups.filter(p => !p.deleted).length;
  const uploadingMediaCount = getUploadingMediaCount(state.postGroups);
  const id = useMemo(() => idGenerator('modalFooter'), []);
  const [addAnother, setAddAnother] = useState<boolean>(
    size !== 'small' &&
      size !== 'medium' &&
      [PostEditorContextEnum.new, PostEditorContextEnum.duplicate].includes(context) &&
      !isFromChromeExtension
  );
  const addAnotherWording = context === PostEditorContextEnum.duplicate ? 'Add another copy' : 'Add another';

  const wrapperClasses = classNames(classes.PostEditorModalFooter, 'grid-x', 'align-right', 'align-middle');
  const importColClasses = classNames(classes.PostEditorModalFooter, 'grid-x', 'align-left', 'align-middle');

  useEffect(() => {
    setAddAnother(
      size !== 'small' &&
        size !== 'medium' &&
        [PostEditorContextEnum.new, PostEditorContextEnum.duplicate].includes(context) &&
        !isFromChromeExtension
    );
  }, [size, context]);

  return context ? (
    <>
      {!!totalErrors && (
        <div className={'cell shrink'}>
          <div className={importColClasses}>
            <div className={'cell auto'}>
              <div className={classes.NotificationWrapper}>
                <div className={'grid-x align-middle'}>
                  <div className={classNames('cell', 'shrink', classes.NoPadding)}>
                    <Icon name={IconsEnum.exclamation} size={'large'} />
                  </div>
                  <div
                    className={classNames('cell', 'shrink', classes.NoPadding)}>{`You have ${totalErrors} ${pluralize(
                    'error',
                    totalErrors
                  )}`}</div>
                  <div className={classNames('cell', 'shrink', classes.NoPadding)}>
                    <Button type={'submit'} variant={'hollow'} size={'small'} color={ButtonColorsEnum.alert}>
                      Show Error
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {debugMode ? (
        <div className={'cell shrink relative'}>
          <Debugger tooltipContent={`Post Editor State`} modalContent={state} />
        </div>
      ) : null}
      <div className={'cell auto'}>
        <div className={wrapperClasses}>
          {addAnother && (
            <>
              <div id={`tooltipAddAnother${id}`} className={'cell shrink'}>
                <Tooltip
                  target={`#tooltipAddAnother${id}`}
                  position={'top'}
                  pointer={true}
                  type={'inlined'}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  After saving, create another Post with the same Category and Profiles.
                </Tooltip>
              </div>
              <div className={'cell shrink'}>
                <Checkbox
                  disabled={loading}
                  formInstance={formInstance}
                  name={'addAnother'}
                  rules={{}}
                  size={'small'}
                  label={addAnotherWording}
                />
              </div>
            </>
          )}
          {uploadingMediaCount ? (
            <div className={'cell shrink'}>
              <Tooltip
                target={`#tooltipTargetMediaUploading${id}`}
                position={'top'}
                pointer={true}
                type={'inlined'}
                autoZIndex={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}>
                Please wait for all media to finish uploading before you submit...
              </Tooltip>
            </div>
          ) : null}
          <div className={'cell shrink'}>
            <Button
              disabled={!!uploadingMediaCount}
              loading={loading}
              type={'submit'}
              size={'large'}
              variant={'solid'}
              color={ButtonColorsEnum.secondary}>
              {context === PostEditorContextEnum.import
                ? `Save ${pluralize('Post', numberOfPosts, true)}`
                : 'Save Post'}
            </Button>
          </div>
        </div>
      </div>
    </>
  ) : (
    <BoxNotification type={'error'}>Post Editor Context not provided !</BoxNotification>
  );
};
